import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { CommunityContentAccessService } from '../../../../community-content/community-content-access.service';
import { HdLineCompanionPanelService } from '../../../../community-content/line-companion/hd-line-companion-panel.service';
import { HdChartSettingsStore } from '../../chart-tabs/hd-chart-settings.store';
import { HdIChingAccessService } from '../../../../iching/hd-iching-access.service';
import { HdIChingPanelService } from '../../../../iching/panel/hd-iching-panel.service';
import { IChingLinePosition } from '../../../../iching/core/iching.models';
import { HdGateLineViewModel } from './hd-gate-line.view-model';
import { HdGateLineDualityRowComponent } from './hd-gate-line-duality-row.component';
import { HdOwnLineTextComponent } from '../../../../line-text-review/hd-own-line-text.component';

/**
 * Card-weergave van één lijn: vaste afmeting (portret), header met groot lijnnummer
 * en titelblok, main-content (subText, blauwe learningAbout, exaltation/detriment),
 * zodiac-footer en een groot subtiel grijs poortnummer als achtergrond-fill dat
 * uitlijnt op de rechter-onderhoek (Bw Modelica Black).
 *
 * Pure presentational component — deelt hetzelfde {@link HdGateLineViewModel} als de
 * horizontale lijn-variant.
 */
@Component({
  selector: 'hd-gate-line-card',
  templateUrl: './hd-gate-line-card.component.html',
  styleUrl: './hd-gate-line-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateLineDualityRowComponent, NgClass, HdOwnLineTextComponent],
  host: {
    '[class.hd-gate-line-card--compact]': 'compact()',
  },
})
export class HdGateLineCardComponent {
  readonly viewModel = input.required<HdGateLineViewModel>();

  /** Compacte (kleinere) kaartvariant: lagere hoogte (375px i.p.v. 480px). */
  readonly compact = input(false);

  /** Toggle voor "extra info" (subText, learningAboutExtended). Default uit. */
  protected readonly showExtra = signal(false);

  private readonly settings = inject(HdChartSettingsStore);

  /**
   * Alternatieve I Tjing-interpretaties (The Book of Lines, Chetan Parkyn) bij de
   * exaltation/detriment tonen — globale setting, default uit. Los van de
   * "extra info"-oog-toggle: staat de setting aan, dan staat de `~`-regel er
   * meteen bij.
   */
  protected readonly showAlternativeIChing = this.settings.alternativeIChingEnabled;

  /**
   * Ra-lijnteksten (blauwe lijnbeschrijving, subtekst/vervolg en de White/Black
   * Book-keynotes) tonen; globale setting, default aan.
   */
  protected readonly showRa = this.settings.raLineTextsEnabled;

  /**
   * Eigen lijnteksten (Mechanics in Motion, ◆ in amber-inkt) tonen; globale
   * setting, default uit. Los te combineren met de Ra- en Chetan-teksten.
   */
  protected readonly showOwn = this.settings.ownLineTextsEnabled;

  /**
   * Heeft deze lijn extra info? Zo niet, dan tonen we de toggle-knop niet. De
   * extra info is Ra-tekst, dus zonder Ra-lijnteksten ook geen knop.
   */
  protected readonly hasExtra = computed<boolean>(() => {
    const model = this.viewModel();
    return this.showRa() && Boolean(model.subText || model.learningAboutExtended);
  });

  protected toggleExtra(): void {
    this.showExtra.update(value => !value);
  }

  private readonly communityAccess = inject(CommunityContentAccessService);
  private readonly lineCompanionPanel = inject(HdLineCompanionPanelService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Line Companion-icoon in de footer (community-content: feature + opt-in). */
  protected readonly lineCompanionVisible = computed(() => this.communityAccess.lineCompanionVisible());

  protected readonly lineCompanionLabel = computed(() => {
    this.contentLang.translationsLoadedTick();
    const model = this.viewModel();
    return this.transloco.translate(
      'humanDesign.chart.lineCompanion.lineTooltip',
      { gate: model.gate, line: model.lineNumber },
      this.contentLang.effectiveLang(),
    );
  });

  protected openLineCompanion(event: MouseEvent): void {
    const model = this.viewModel();
    this.lineCompanionPanel.openGate(model.gate, {
      focusLine: model.lineNumber,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);

  /** Classic I Ching-icoon in de footer (feature + setting). */
  protected readonly classicIChingVisible = this.ichingAccess.classicVisible;

  protected readonly classicIChingLabel = computed(() => {
    this.contentLang.translationsLoadedTick();
    const model = this.viewModel();
    return this.transloco.translate(
      'humanDesign.chart.classicIChing.lineTooltip',
      { gate: model.gate, line: model.lineNumber },
      this.contentLang.effectiveLang(),
    );
  });

  protected openClassicIChing(event: MouseEvent): void {
    const model = this.viewModel();
    this.ichingPanel.openHexagram(model.gate, {
      focusLine: model.lineNumber as IChingLinePosition,
      origin: { x: event.clientX, y: event.clientY },
    });
  }
}
